<template>
  <div class="max-w-4xl mx-auto">
    <Head title="Crear Ticket de Soporte" />

    <!-- Breadcrumb & Header -->
    <div class="mb-6">
      <div class="flex items-center gap-3 text-sm text-gray-500 mb-2">
        <Link href="/tickets" class="hover:text-orange-500 transition-colors flex items-center gap-1.5 font-medium">
          <i class="fa fa-arrow-left text-xs" />
          <span>Tickets</span>
        </Link>
        <span class="text-gray-300 font-light">/</span>
        <span class="text-gray-900 font-medium">Nuevo Ticket</span>
      </div>
      
      <h1 class="text-2xl font-bold text-gray-900">Crear Nuevo Ticket de Soporte</h1>
      <p class="text-gray-500 mt-1 text-sm">Describe con detalle tu solicitud para que nuestro equipo de soporte pueda ayudarte lo antes posible.</p>
    </div>

    <!-- Form Card -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
      <div class="p-6 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2.5">
        <i class="fa fa-headset text-orange-500 text-lg" />
        <h3 class="text-base font-semibold text-gray-900">Detalles de la Solicitud</h3>
      </div>

      <form class="p-6 sm:p-8" @submit.prevent="submit">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Asunto -->
          <div class="md:col-span-2">
            <label for="subject" class="block text-sm font-medium text-gray-700 mb-1.5">
              Asunto del Ticket <span class="text-red-500">*</span>
            </label>
            <input 
              id="subject" 
              v-model="form.subject" 
              type="text" 
              class="block w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm text-gray-900 placeholder-gray-400 transition-colors" 
              placeholder="Ej: Inconveniente al sincronizar inventario o registrar ventas..."
              required
            />
            <p v-if="form.errors.subject" class="mt-1.5 text-xs text-red-600 font-medium">{{ form.errors.subject }}</p>
          </div>

          <!-- Categoría -->
          <div>
            <label for="category" class="block text-sm font-medium text-gray-700 mb-1.5">
              Categoría <span class="text-red-500">*</span>
            </label>
            <div class="relative">
              <select 
                id="category" 
                v-model="form.category" 
                class="block w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm text-gray-700 transition-colors appearance-none"
                required
              >
                <option value="" disabled>Selecciona una categoría</option>
                <option value="Error técnico">Error técnico / Bug</option>
                <option value="Facturación">Facturación y Pagos</option>
                <option value="Configuración">Configuración y Usuarios</option>
                <option value="Sugerencia">Sugerencia de Mejora</option>
                <option value="Otro">Otro Asunto</option>
              </select>
              <div class="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-gray-400">
                <i class="fa fa-chevron-down text-xs" />
              </div>
            </div>
            <p v-if="form.errors.category" class="mt-1.5 text-xs text-red-600 font-medium">{{ form.errors.category }}</p>
          </div>

          <!-- Prioridad -->
          <div>
            <label for="priority" class="block text-sm font-medium text-gray-700 mb-1.5">
              Prioridad <span class="text-red-500">*</span>
            </label>
            <div class="relative">
              <select 
                id="priority" 
                v-model="form.priority" 
                class="block w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm text-gray-700 transition-colors appearance-none"
                required
              >
                <option value="" disabled>Selecciona una prioridad</option>
                <option value="Baja">Baja (Consultas generales o menores)</option>
                <option value="Media">Media (Uso interrumpido no crítico)</option>
                <option value="Alta">Alta / Urgente (Bloqueo de operaciones)</option>
              </select>
              <div class="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-gray-400">
                <i class="fa fa-chevron-down text-xs" />
              </div>
            </div>
            <p v-if="form.errors.priority" class="mt-1.5 text-xs text-red-600 font-medium">{{ form.errors.priority }}</p>
          </div>

          <!-- Descripción -->
          <div class="md:col-span-2">
            <label for="message" class="block text-sm font-medium text-gray-700 mb-1.5">
              Descripción detallada <span class="text-red-500">*</span>
            </label>
            <textarea 
              id="message" 
              v-model="form.message" 
              rows="5" 
              class="block w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm text-gray-900 placeholder-gray-400 transition-colors"
              placeholder="Explica qué ocurrió, los pasos para reproducirlo y cualquier detalle relevante..."
              required
            />
            <p v-if="form.errors.message" class="mt-1.5 text-xs text-red-600 font-medium">{{ form.errors.message }}</p>
          </div>

          <!-- Archivo Adjunto -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1.5">
              Archivo Adjunto o Captura <span class="text-xs text-gray-400 font-normal">(Opcional)</span>
            </label>
            <div class="border-2 border-dashed border-gray-200 hover:border-orange-400 rounded-lg p-5 text-center transition-colors bg-gray-50/50">
              <input 
                id="ticket-file" 
                type="file" 
                class="hidden"
                accept="image/*,.pdf,.doc,.docx,.xlsx"
                @change="handleFile"
              />
              <label for="ticket-file" class="cursor-pointer flex flex-col items-center">
                <i class="fa fa-cloud-upload-alt text-2xl text-orange-400 mb-1.5" />
                <span class="text-sm font-medium text-gray-700">Haz clic para adjuntar imagen o documento</span>
                <span class="text-xs text-gray-400 mt-0.5">Soporta capturas JPG, PNG o documentos PDF</span>
                <div v-if="form.attachment" class="mt-2.5 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-semibold flex items-center gap-1.5">
                  <i class="fa fa-paperclip" />
                  <span>{{ form.attachment.name }}</span>
                </div>
              </label>
            </div>
          </div>
        </div>

        <!-- Botones de Acción -->
        <div class="mt-8 pt-6 border-t border-gray-100 flex items-center justify-end gap-3">
          <Link 
            href="/tickets" 
            class="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg font-medium text-sm transition-colors shadow-sm cursor-pointer"
          >
            Cancelar
          </Link>
          
          <loading-button 
            :loading="form.processing"
            type="submit" 
            class="px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium text-sm transition-colors shadow-sm flex items-center gap-2"
          >
            <i class="fa fa-paper-plane text-xs" />
            <span>Enviar Ticket</span>
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  name: 'TicketsCreate',

  components: {
    Head,
    Link,
    LoadingButton,
  },

  layout: Layout,

  data() {
    return {
      form: this.$inertia.form({
        subject: '',
        category: '',
        priority: 'Media',
        message: '',
        attachment: null,
      }),
    }
  },

  methods: {
    handleFile(e) {
      this.form.attachment = e.target.files[0]
    },

    submit() {
      this.form.post('/tickets', {
        preserveScroll: true,
      })
    },
  },
}
</script>
